import { useState, useEffect, useCallback } from 'react';
import { enhancedApi } from '../../../shared/services/enhancedApi';

export interface LocalAddressBarangay {
  barangayCityMunId: number;
  provinceCode: string;
  cityMunCode: string;
  barangayDescription: string;
  zipCode: string;
  createdBy: string | null;
  createdDate: string | null;
  updatedBy: string | null;
  updatedDate: string | null;
}

interface UseLocalAddressesBarangaysReturn {
  barangays: LocalAddressBarangay[];
  barangayOptions: string[];
  loading: boolean;
  error: string | null;
  refetch: (cityMunCode: string) => Promise<void>;
}

export const useLocalAddressesBarangays = (cityMunCode?: string): UseLocalAddressesBarangaysReturn => {
  const [barangays, setBarangays] = useState<LocalAddressBarangay[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBarangays = useCallback(async (city: string) => {
    if (!city) {
      setBarangays([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const encodedCity = encodeURIComponent(city);
      
      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<{
        success: boolean;
        barangays: LocalAddressBarangay[];
      }>(
        `/api/local-addresses/barangays?city=${encodedCity}`,
        {},
        10 * 60 * 1000 // 10 minutes TTL - barangays change less frequently but are more dynamic than provinces
      );

      if (response.success && response.barangays) {
        const sortedBarangays = [...response.barangays].sort((a, b) =>
          a.barangayDescription.localeCompare(b.barangayDescription)
        );

        console.log("Fetched barangays (cached/sorted):", sortedBarangays);

        setBarangays(sortedBarangays);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching barangays:', err);
      setError('Failed to fetch barangays');
      setBarangays([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cityMunCode) {
      fetchBarangays(cityMunCode);
    } else {
      setBarangays([]);
    }
  }, [cityMunCode, fetchBarangays]);

  // Convert barangays to location options format for dropdowns
  const barangayOptions = barangays.map(barangay => barangay.barangayDescription);

  return {
    barangays,
    barangayOptions,
    loading,
    error,
    refetch: fetchBarangays
  };
};